// Terms acceptance gate strings — EN + TR.
//
// Used by `features/onboarding/TermsAcceptanceGate.tsx` (non-dismissible
// agreement gate: signing / waiting / unavailable / success screens). The
// agreement title comes from the backend; sentences around it are split into
// before/after parts so the title can stay a clickable element.
// Server-side validation messages are shown as returned by the backend.

import type { LocalizedDict } from '../index'

type TermsAcceptanceStrings = {
  footer: {
    needHelp: string
    contactSupport: string
    logOut: string
  }
  waiting: {
    title: string
    mustAcceptBefore: string
    mustAcceptAfter: string
    fallbackAgreement: string
    waitingNote: string
  }
  unavailable: {
    title: string
    body: string
    retry: string
  }
  signing: {
    title: string
    fallbackEntity: string
    introBefore: (entity: string) => string
    introAfter: string
    fallbackAgreement: string
    conflict: string
    fullNameLabel: string
    signerTitleLabel: string
    signerTitlePlaceholder: string
    legalNameLabel: string
    addressLabel: string
    countryLabel: string
    stateLabel: string
    authorityConsent: (entity: string) => string
    electronicConsent: string
    programConsent: string
    signatureLabel: string
    passwordLabel: string
    submitting: string
    submit: string
  }
  errors: {
    submissionFailed: string
    fullNameRequired: string
    legalNameRequired: string
    countryRequired: string
    required: string
    signatureRequired: string
    passwordRequired: string
  }
  success: {
    title: string
    body: string
    download: string
    continue: string
    downloadError: string
  }
}

export const termsAcceptanceI18n: LocalizedDict<TermsAcceptanceStrings> = {
  en: {
    footer: {
      needHelp: 'Need help?',
      contactSupport: 'Contact support',
      logOut: 'Log out',
    },
    waiting: {
      title: 'Agreement acceptance required',
      mustAcceptBefore: 'Your organization must accept ',
      mustAcceptAfter: ' before this account can continue.',
      fallbackAgreement: 'an agreement',
      waitingNote:
        'Waiting for an authorized account owner or administrator to accept the terms. You will regain access as soon as it is signed.',
    },
    unavailable: {
      title: 'We can’t verify your account right now',
      body: 'We can’t verify your account’s agreement status right now. This is usually temporary — please retry in a moment.',
      retry: 'Retry',
    },
    signing: {
      title: 'Accept your organization’s agreement',
      fallbackEntity: 'your organization',
      introBefore: (entity) => `Before continuing, ${entity} must accept `,
      introAfter: '.',
      fallbackAgreement: 'the Terms and Conditions',
      conflict:
        'This agreement was already submitted with different details. We’ve refreshed your status — if the gate does not close, please retry.',
      fullNameLabel: 'Your full name',
      signerTitleLabel: 'Signer title',
      signerTitlePlaceholder: 'e.g. Owner',
      legalNameLabel: 'Legal entity name',
      addressLabel: 'Company address',
      countryLabel: 'Country',
      stateLabel: 'State / region',
      authorityConsent: (entity) =>
        `I represent and warrant that I am authorized to bind ${entity} to this Agreement.`,
      electronicConsent: 'I consent to sign this Agreement electronically.',
      programConsent: 'I acknowledge the reseller / vendor program terms.',
      signatureLabel: 'Signature',
      passwordLabel: 'Confirm your account password',
      submitting: 'Submitting…',
      submit: 'Accept & sign',
    },
    errors: {
      submissionFailed: 'Submission failed. Please retry.',
      fullNameRequired: 'Your full name is required.',
      legalNameRequired: 'Legal entity name is required.',
      countryRequired: 'A 2-letter country code is required.',
      required: 'Required.',
      signatureRequired: 'A signature is required.',
      passwordRequired: 'Your account password is required.',
    },
    success: {
      title: 'Agreement accepted',
      body: 'Thank you — your organization’s agreement has been recorded. A signed copy is available for your records.',
      download: 'Download signed PDF',
      continue: 'Continue to PEXIFY',
      downloadError:
        'The signed copy could not be retrieved right now. You can download it later from your account.',
    },
  },
  tr: {
    footer: {
      needHelp: 'Yardım mı lazım?',
      contactSupport: 'Destekle iletişime geçin',
      logOut: 'Çıkış yap',
    },
    waiting: {
      title: 'Sözleşme onayı gerekiyor',
      mustAcceptBefore: 'Bu hesabın devam edebilmesi için kuruluşunuzun ',
      mustAcceptAfter: ' belgesini kabul etmesi gerekiyor.',
      fallbackAgreement: 'bir sözleşme',
      waitingNote:
        'Yetkili bir hesap sahibinin veya yöneticinin şartları kabul etmesi bekleniyor. İmzalanır imzalanmaz erişiminiz geri açılacak.',
    },
    unavailable: {
      title: 'Hesabınızı şu anda doğrulayamıyoruz',
      body: 'Hesabınızın sözleşme durumunu şu anda doğrulayamıyoruz. Bu genelde geçicidir — lütfen biraz sonra tekrar deneyin.',
      retry: 'Tekrar dene',
    },
    signing: {
      title: 'Kuruluşunuzun sözleşmesini kabul edin',
      fallbackEntity: 'kuruluşunuz',
      introBefore: (entity) => `Devam etmeden önce ${entity} adına şu belgenin kabul edilmesi gerekiyor: `,
      introAfter: '.',
      fallbackAgreement: 'Şartlar ve Koşullar',
      conflict:
        'Bu sözleşme daha önce farklı bilgilerle gönderilmiş. Durumunuzu yeniledik — pencere kapanmazsa lütfen tekrar deneyin.',
      fullNameLabel: 'Adınız soyadınız',
      signerTitleLabel: 'İmzalayanın unvanı',
      signerTitlePlaceholder: 'örn. Sahibi',
      legalNameLabel: 'Resmi şirket adı',
      addressLabel: 'Şirket adresi',
      countryLabel: 'Ülke',
      stateLabel: 'Eyalet / bölge',
      authorityConsent: (entity) =>
        `${entity} adına bu Sözleşmeyi imzalamaya yetkili olduğumu beyan ve taahhüt ederim.`,
      electronicConsent: 'Bu Sözleşmeyi elektronik olarak imzalamayı kabul ediyorum.',
      programConsent: 'Bayi / tedarikçi programı şartlarını okudum ve kabul ediyorum.',
      signatureLabel: 'İmza',
      passwordLabel: 'Hesap şifrenizi onaylayın',
      submitting: 'Gönderiliyor…',
      submit: 'Kabul et ve imzala',
    },
    errors: {
      submissionFailed: 'Gönderilemedi. Lütfen tekrar deneyin.',
      fullNameRequired: 'Adınız soyadınız gerekli.',
      legalNameRequired: 'Resmi şirket adı gerekli.',
      countryRequired: '2 harfli ülke kodu gerekli.',
      required: 'Zorunlu.',
      signatureRequired: 'İmza gerekli.',
      passwordRequired: 'Hesap şifreniz gerekli.',
    },
    success: {
      title: 'Sözleşme kabul edildi',
      body: 'Teşekkürler — kuruluşunuzun sözleşmesi kaydedildi. İmzalı bir kopyayı kayıtlarınız için indirebilirsiniz.',
      download: "İmzalı PDF'i indir",
      continue: "PEXIFY'a devam et",
      downloadError:
        'İmzalı kopya şu anda alınamadı. Daha sonra hesabınızdan indirebilirsiniz.',
    },
  },
}
